import { BriefcaseBusiness, ChevronDown, Home, User } from 'lucide-react';
import { MdLogout } from 'react-icons/md';
import { cn } from '..';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar } from './ui/avatar';

export interface ClientUserMenuAction {
  label: string;
  description?: string;
  onClick: () => void;
}

interface ClientUserMenuProps {
  handleClickSignOut: () => void;
  clientEmail: string;
  onNavigateHome?: () => void;
  advisorAccountAction?: ClientUserMenuAction;
  showEmail?: boolean;
}

export function ClientUserMenu({
  handleClickSignOut,
  clientEmail,
  onNavigateHome,
  advisorAccountAction,
  showEmail = true,
}: ClientUserMenuProps) {
  const truncatedEmail =
    clientEmail.length > 25 ? `${clientEmail.substring(0, 25)}...` : clientEmail;
  const buttonStyle =
    'group flex min-h-11 w-full gap-x-3 rounded-md p-2 text-left text-sm font-medium leading-6 text-gray-800 hover:bg-primary-50';

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={cn(
          'flex min-h-11 items-center rounded-full bg-white',
          showEmail ? 'gap-2 px-3 py-1.5' : 'p-2',
        )}
        aria-label="Open account menu"
      >
        <Avatar className="bg-primary-100 h-8 w-8 items-center justify-center">
          <User
            className="text-primary-dark"
            size={18}
          />
        </Avatar>
        {showEmail && (
          <>
            <span className="text-primary-dark text-sm">{truncatedEmail}</span>
            <ChevronDown className="text-primary-dark h-4 w-4" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) rounded-xl p-3"
        align="end"
        side="bottom"
        sideOffset={5}
      >
        {onNavigateHome && (
          <DropdownMenuItem asChild>
            <button
              className={buttonStyle}
              onClick={onNavigateHome}
            >
              <Home className="h-6 w-6 shrink-0 text-gray-800" />
              Home
            </button>
          </DropdownMenuItem>
        )}
        {advisorAccountAction && (
          <DropdownMenuItem asChild>
            <button
              className={buttonStyle}
              onClick={advisorAccountAction.onClick}
            >
              <BriefcaseBusiness className="text-primary-dark mt-0.5 h-6 w-6 shrink-0" />
              <span>
                <span className="block leading-5">{advisorAccountAction.label}</span>
                {advisorAccountAction.description && (
                  <span className="block text-xs font-normal leading-4 text-gray-600">
                    {advisorAccountAction.description}
                  </span>
                )}
              </span>
            </button>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <button
            className={buttonStyle}
            onClick={handleClickSignOut}
          >
            <MdLogout className="h-6 w-6 shrink-0 text-gray-800" />
            Sign Out
          </button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
